/**
 * Assembles the promo data based on the given promo data and plan data.
 *
 * @param {ValidPromoData} promoData - The promo data to be used for assembly.
 * @param {PlanData} planData - The plan data to be used for assembly.
 * @returns {RetrievedPromoData} - The assembled promo data.
 */
export const onePlatformAssemblePromoData = (promoData, planData) => {
    return planData ? buildPromoData(planData, promoData) : buildDefaultPromoData(planData);
};
/**
 * Builds the retrieved promo data based on the given plan data and promo data.
 * @param {PlanData} planData - The plan data.
 * @param {ValidPromoData} promoData - The promo data.
 * @returns {RetrievedPromoData} The retrieved promo data.
 */
const buildPromoData = (planData, promoData) => {
    var _a, _b;
    const actualPrice = planData.data[0].attributes.field_plan_monthly_fee;
    const onePlatformId = planData.data[0].attributes.field_plan_one_platform_id;
    const promoDuration = getPromoDuration(promoData);
    let build = {
        promo_duration: '',
        promo_name: '',
        promo_end_date: '',
        promo_price: 0,
        promo_tile: ''
    };
    build.promo_duration = (_a = promoDuration === null || promoDuration === void 0 ? void 0 : promoDuration.toString()) !== null && _a !== void 0 ? _a : '';
    build.promo_name = promoData.name;
    build.promo_end_date = promoData.validFor.endDateTime;
    if (!((_b = promoData.pattern[0].action) === null || _b === void 0 ? void 0 : _b.length)) {
        return build;
    }
    promoData.pattern[0].action.forEach(action => {
        var _a;
        const actionObjectId = (_a = action.actionObjectId) !== null && _a !== void 0 ? _a : '';
        if (actionObjectId !== onePlatformId) {
            return;
        }
        build = Object.assign(Object.assign({}, build), pricingStrategy(action, actualPrice, promoDuration));
    });
    return build;
};
/**
 * Builds the default promo data for a given plan data.
 *
 * @param {PlanData} planData - The plan data object.
 * @returns {RetrievedPromoData} - The built default promo data.
 */
const buildDefaultPromoData = (planData) => {
    return {
        promo_end_date: planData.data[0].attributes.field_plan_end_date,
        promo_duration: '6',
        promo_name: '',
        promo_tile: '',
        promo_price: 0
    };
};
/**
 * Calculates the duration of a promo based on the given valid promo data.
 *
 * @param {ValidPromoData} data - The valid promo data.
 * @returns {number | null} The duration of the promo in periods, or null if not found.
 */
const getPromoDuration = (data) => {
    var _a, _b;
    const relatedEntity = (_a = data === null || data === void 0 ? void 0 : data.relatedEntity) !== null && _a !== void 0 ? _a : [];
    for (const item of relatedEntity) {
        const values = Array.isArray(item.value) ? item.value : [];
        for (const subItem of values) {
            if (subItem.name === "upToPeriods") {
                return (_b = subItem.value) !== null && _b !== void 0 ? _b : null;
            }
        }
    }
    return null;
};
/**
 * Applies a pricing strategy based on the provided action value and type.
 *
 * @param {ValidPromoAction} value - The action value and type to determine the pricing strategy.
 * @param {string} actualPrice - The actual price of the product or service.
 * @param {number} promoDuration - The duration of the promotional period.
 * @returns {object} - An object representing the pricing strategy applied.
 */
export const pricingStrategy = (value, actualPrice, promoDuration) => {
    const actionValue = value.actionValue;
    const actionType = value.type;
    if (actionType === 'fixedAmount') {
        return fixedAmountStrategy(actionValue, actualPrice, promoDuration);
    }
    else if (actionType === 'percent') {
        return percentStrategy(actionValue, actualPrice);
    }
    return {};
};
/**
 * Calculates promotional price and promotional tile based on action value,
 * actual price, and promotional duration.
 *
 * @param {string} actionValue - The value of the action
 * @param {string} actualPrice - The actual price
 * @param {number} promoDuration - The duration of the promotion
 * @returns {object} - An object containing the promotional price and promotional tile
 */
const fixedAmountStrategy = (actionValue, actualPrice, promoDuration) => {
    const promoPrice = +actualPrice - +actionValue;
    let promoTile = '';
    if (promoDuration > 1) {
        promoTile = `<b>Save $${actionValue}/month</b> for ${promoDuration} months`;
    }
    else if (promoDuration === 1) {
        promoTile = `<b>Save $${actionValue}/month</b> for first month`;
    }
    return {
        promo_price: promoPrice,
        promo_tile: promoTile
    };
};
/**
 * Calculate promotional price and promotional title based on the given action value and actual price.
 *
 * @param {string} actionValue - The value of the action percentage.
 * @param {string} actualPrice - The actual price of the product.
 * @returns {object} - An object containing the promotional price and promotional title.
 */
const percentStrategy = (actionValue, actualPrice) => {
    const percentagePrice = (+actionValue / 100) * +actualPrice;
    const promoPrice = +actualPrice - percentagePrice;
    const promoTile = `<b>Save ${actionValue}%</b> per month`;
    return {
        promo_price: promoPrice,
        promo_tile: promoTile
    };
};
export default onePlatformAssemblePromoData;
